import React, { useState } from 'react'
import { FleetSection } from '../../../../shared/sso-workspace/fleet-page'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { OrgPicker } from '../../../../shared/org-search/org-picker'
import { formatCount, plural } from '../../../../shared/sso-workspace/format'
import { drainDeliveries } from '../../../../api-new/state/notify'
import { PROVIDERS, providerLabel } from '../../../../shared/notify/words'
import { DeliveryLog } from './delivery-log'
import { ReasonDialog } from './reason-dialog'
import { useNotifyOpsAccess } from './access'

const ALL_ORGANIZATIONS = { value: '', label: 'Every organization' }
// notify resends at most this many per drain call.
export const DRAIN_CAP = 10000
const NO_DRAIN = { provider: '', type: '', organizationId: '', from: '', to: '' }

const dayStart = v => {
  const [y, m, d] = v.split('-').map(Number)
  return new Date(y, m - 1, d).toISOString()
}
const dayAfter = v => {
  const [y, m, d] = v.split('-').map(Number)
  return new Date(y, m - 1, d + 1).toISOString()
}

// The C-4 DrainRequest for a filter: blanks left out, days as instants.
export const drainBody = (f, dryRun, reason) => {
  const body = { dryRun }
  if (f.provider) body.provider = f.provider
  if (f.type && f.type.trim()) body.type = f.type.trim()
  if (f.organizationId) body.organizationId = Number(f.organizationId)
  if (f.from) body.since = dayStart(f.from)
  if (f.to) body.until = dayAfter(f.to)
  if (reason) body.reason = reason
  return body
}

// The dead letter (spec section 13.1): every failed delivery, with resend
// and park on each row (the delivery log's), and Drain: resend every failed
// delivery matching a filter. Drain always counts first (dryRun), and only
// then offers to resend that many (at most 10,000 a call; one audited
// summary). Deliveries whose notification expired, whose address is
// suppressed or whose organization is gone are skipped by the server.
export const DeadLetterTab = () => {
  const access = useNotifyOpsAccess()
  const [filter, setFilter] = useState(NO_DRAIN)
  const [count, setCount] = useState(null) // DrainResult of the last dry run, for this filter
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const [confirm, setConfirm] = useState(false)
  const [confirmError, confirmFailure, setConfirmError] = useErrorLine()
  const [done, setDone] = useState(null)
  const [generation, setGeneration] = useState(0)

  const set = (key, value) => {
    setFilter(f => ({ ...f, [key]: value }))
    setCount(null)
    setDone(null)
  }

  const countMatching = async () => {
    setBusy(true)
    setError(null)
    setDone(null)
    try {
      setCount(await drainDeliveries(drainBody(filter, true)))
    } catch (err) {
      setError(serverMessage(err, "The failed deliveries couldn't be counted."), err)
    } finally {
      setBusy(false)
    }
  }

  const drain = async reason => {
    setBusy(true)
    setConfirmError(null)
    try {
      const result = await drainDeliveries(drainBody(filter, false, reason))
      setDone(result || { resent: 0, skipped: 0 })
      setConfirm(false)
      setCount(null)
      setGeneration(g => g + 1)
    } catch (err) {
      setConfirmError(serverMessage(err, "The failed deliveries couldn't be resent."), err)
    } finally {
      setBusy(false)
    }
  }

  const matched = count ? count.matched || 0 : 0
  const resendable = Math.min(matched, DRAIN_CAP)

  return (
    <>
      {access.canUpdate &&
        <FleetSection id="nf-drain" title="Drain" lede="Resend every failed delivery that matches, after a provider outage or a credential fix. Count first; nothing is sent until you confirm.">
          <div className="nf-toolbar">
            <span className="nf-org">
              <OrgPicker id="nf-drain-org" label="Drain organization" value={filter.organizationId} onChange={v => set('organizationId', v)} allOption={ALL_ORGANIZATIONS} small />
            </span>
            <select className="form-select form-select-sm" aria-label="Drain provider" value={filter.provider} onChange={e => set('provider', e.target.value)}>
              <option value="">Every provider</option>
              {PROVIDERS.filter(p => p !== 'capture').map(p => <option key={p} value={p}>{providerLabel(p)}</option>)}
            </select>
            <span className="nf-field">
              <label htmlFor="nf-drain-type">Type</label>
              <input id="nf-drain-type" type="text" className="form-control form-control-sm" placeholder="course.updated" value={filter.type} onChange={e => set('type', e.target.value)} />
            </span>
            <span className="nf-field">
              <label htmlFor="nf-drain-from">Failed from</label>
              <input id="nf-drain-from" type="date" className="form-control form-control-sm" value={filter.from} onChange={e => set('from', e.target.value)} />
            </span>
            <span className="nf-field">
              <label htmlFor="nf-drain-to">Failed to</label>
              <input id="nf-drain-to" type="date" className="form-control form-control-sm" value={filter.to} onChange={e => set('to', e.target.value)} />
            </span>
            <button type="button" className="btn btn-sm sw-btn" disabled={busy} onClick={countMatching}>Count matching</button>
          </div>
          {count && (matched > 0
            ? <div className="d-flex flex-wrap align-items-center gap-2">
                <span>{`${plural(matched, 'failed delivery', 'failed deliveries')} match.`}</span>
                <button type="button" className="btn btn-sm sw-btn-primary" disabled={busy} onClick={() => { setConfirmError(null); setConfirm(true) }}>{`Resend ${formatCount(resendable)}`}</button>
                {matched > DRAIN_CAP && <span className="sw-hint small w-100">{`Each drain resends at most ${formatCount(DRAIN_CAP)}. Drain again for the rest.`}</span>}
              </div>
            : <p className="sw-hint m-0">No failed deliveries match.</p>)}
          {done && <p className="text-success small mt-2" role="status">{`Resent ${formatCount(done.resent || 0)}. Skipped ${formatCount(done.skipped || 0)} (expired, suppressed or their organization is gone).`}</p>}
          {error && <p className="text-danger small mt-2" role="alert">{error}</p>}
          {error && <ErrorReference error={failure} />}
        </FleetSection>}

      <DeliveryLog key={generation} system fixed={{ status: 'failed' }} allowStatus={false} title="Dead letter" exportName="notify-dead-letter"
        lede="Deliveries that ran out of retries or were refused for good, newest first. Resend one, or park it so it stays out of the next drain. Search by trace id, login id or type." />

      <ReasonDialog
        open={confirm}
        title={`Resend ${plural(resendable, 'failed delivery', 'failed deliveries')}?`}
        body="Each one is sent again as a new delivery. The originals stay as they are. This is recorded as one change."
        confirmText="Resend them"
        busy={busy}
        error={confirmError}
        failure={confirmFailure}
        onConfirm={drain}
        onCancel={() => setConfirm(false)}
      />
    </>
  )
}
